import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import toast from 'react-hot-toast';
import { apiFetch } from '../api/client';

const contactSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  email: z.string().email('Valid email is required'),
  organization: z.string().max(200).optional(),
  message: z.string().min(10, 'Message must be at least 10 characters').max(2000),
});

type ContactFormData = z.infer<typeof contactSchema>;

export function ContactPage() {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const [website, setWebsite] = React.useState('');

  const mutation = useMutation({
    mutationFn: (data: ContactFormData) => {
      const payload: Record<string, string> = { ...data };
      if (website) payload.website = website;
      return apiFetch('/api/contact', { method: 'POST', body: JSON.stringify(payload) });
    },
  });

  const onSubmit = (data: ContactFormData) => {
    mutation.mutate(data, {
      onSuccess: () => {
        toast.success('Message sent! We\'ll be in touch soon.');
        reset();
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to send message');
      },
    });
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-display text-spi-charcoal mb-3">
            Get in Touch
          </h1>
          <p className="text-spi-slate text-lg">
            Need a tailored agricultural platform? Let's discuss how Bay Crew Tech can help your organization.
          </p>
        </div>

        <div className="card p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-spi-charcoal mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                {...register('name')}
                className="input w-full"
                placeholder="Your full name"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-spi-charcoal mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                {...register('email')}
                className="input w-full"
                placeholder="you@organization.com"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label htmlFor="organization" className="block text-sm font-medium text-spi-charcoal mb-1">
                Organization
              </label>
              <input
                id="organization"
                type="text"
                {...register('organization')}
                className="input w-full"
                placeholder="Your organization name"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-spi-charcoal mb-1">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                rows={5}
                {...register('message')}
                className="input w-full resize-none"
                placeholder="Tell us about your project and how we can help..."
              />
              {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message.message}</p>}
            </div>

            {/* Honeypot field — invisible to humans, traps bots */}
            <div className="absolute opacity-0 h-0 w-0 -z-10 overflow-hidden" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                id="website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={mutation.isPending}
              className="btn btn-primary w-full disabled:opacity-50"
            >
              {mutation.isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Sending...
                </span>
              ) : (
                'Send Message'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
